import React, { useMemo, useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import ReactDOM from 'react-dom';
import { FormattedMessage } from 'react-intl';
import { Provider as ModalProvider } from 'ui-components/components/modal/context';
import { GlobalEventListenerRoot } from 'ui-components/components/global-event-listener/root';
import { Provider as HotkeyProvider } from 'ui-components/components/hotkey/context';
import { Hotkey } from 'ui-components/components/hotkey';
import { useGlobalHotkeys } from 'ui-components/components/hotkey/hooks';
import { useFocusJail } from 'ui-components/hooks/use-focus-jail';
import { useMaxZ } from 'ui-components/hooks/services/use-max-z';
import { testId } from 'ui-components/services/testid-service';

import CloseModalHotkey from '../hotkeys/close-modal';
import { ConfirmDialogueModal } from '../modals/confirm-dialogue-modal';
import { useModal } from '../../hooks/use-modal';
import { useModalManagerPortal } from '../../portals/hooks';
import { MessageDescriptorPropType } from '../../prop-types/intl';
import { useEnableNewHotkeySystem } from '../../feature-flags/hooks/use-enable-new-hotkey-system';

import style from './index.module.less';

const isCancel = true;
const stopClickPropagation = (event) => event.stopPropagation();
const stopKeyDownPropagation = (event) => event.stopPropagation();

// Tracks open modal portals by z-index so the Escape hotkey closes only the
// topmost modal. With the global event listener removed, every modal's
// close-modal hotkey registers into a single shared registry, so without this
// stack a single Escape would close all open modals at once.
const openModalStack = []; // { zIndex, close }

const closeTopModalPortal = () => {
  if (openModalStack.length === 0) {
    return;
  }
  const top = openModalStack.reduce((highest, entry) => (entry.zIndex > highest.zIndex ? entry : highest));
  top.close();
};

const ModalPortal = ({
  children,
  confirmCancelBodyMessage,
  confirmCancelButtonMessage,
  confirmCancelHeaderMessage,
  onCancel,
  onClose,
  hideCloseButton = false,
  shouldConfirmOnClose = null,
}) => {
  const zIndex = useMaxZ();
  const zIndexStyle = useMemo(() => ({ zIndex }), [zIndex]);
  const modalManagerPortalRef = useModalManagerPortal();
  const isNewHotkeySystemEnabled = useEnableNewHotkeySystem();
  // Callback ref so that wrapperEl triggers a re-render when the DOM element
  // attaches, which lets useGlobalKeyDown (inside HotkeyProvider) re-attach
  // its listener to the element rather than staying on window.
  const [wrapperEl, setWrapperEl] = useState(null);
  const wrapperRef = useMemo(() => ({ current: wrapperEl }), [wrapperEl]);
  const globalHotkeys = useGlobalHotkeys();
  useFocusJail(wrapperRef, { active: isNewHotkeySystemEnabled });

  const { open: onOpenConfirmModal, close: onCloseConfirmModal, isOpen: isConfirmModalOpen } = useModal();

  const onCancelCb = useCallback(() => {
    const hasConfirmMessages = confirmCancelBodyMessage && confirmCancelButtonMessage && confirmCancelHeaderMessage;
    const shouldConfirm = shouldConfirmOnClose ? shouldConfirmOnClose() : hasConfirmMessages;

    if (hasConfirmMessages && shouldConfirm) {
      onOpenConfirmModal();
    } else if (onCancel) {
      onCancel();
    } else {
      onClose(isCancel);
    }
  }, [
    confirmCancelBodyMessage,
    confirmCancelButtonMessage,
    confirmCancelHeaderMessage,
    onCancel,
    onOpenConfirmModal,
    onClose,
    shouldConfirmOnClose,
  ]);

  /**
   * This is dumb because since we provide onClose to the ModalProvider a developer may call something like
   * `const { onClose } = useModalContext();` followed by a `<button onClick={onClose}>Close</button>`
   * which ultimately will feed the onClose event with a synthetic event as the param. So in this case we filter out the
   * boolean and events so we may capture the appropriate booleans.
   * TODO: Once we remove modalFrameworkMethods and standardize our callback convention within modals to do away with this bandaid func.
   * @type {(function(*): void)|*}
   */
  const onCloseCb = useCallback(
    (eventOrBool) => {
      if (typeof eventOrBool === 'boolean') {
        onClose(eventOrBool);
      } else {
        const cancelBandaid = false;
        onClose(cancelBandaid);
      }
    },
    [onClose],
  );

  // Keep a ref to the latest onCancelCb so the stacked entry always closes via
  // current logic (onCancelCb identity changes as its deps change).
  const onCancelRef = useRef(onCancelCb);
  onCancelRef.current = onCancelCb;

  useEffect(() => {
    if (hideCloseButton) {
      return undefined;
    }
    const entry = { zIndex, close: () => onCancelRef.current() };
    openModalStack.push(entry);
    return () => {
      const idx = openModalStack.indexOf(entry);
      if (idx >= 0) {
        openModalStack.splice(idx, 1);
      }
    };
  }, [zIndex, hideCloseButton]);

  if (!modalManagerPortalRef) {
    return null;
  }

  return ReactDOM.createPortal(
    <div className={style.modal} {...testId('component-modal-portal')}>
      <div className={style['modal-background']} style={zIndexStyle} />
      <GlobalEventListenerRoot>
        <div
          className={style['modal-positioning-wrapper']}
          style={zIndexStyle}
          ref={isNewHotkeySystemEnabled ? setWrapperEl : undefined}
          tabIndex={isNewHotkeySystemEnabled ? -1 : undefined}
          onClick={isNewHotkeySystemEnabled ? stopClickPropagation : undefined}
          onKeyDown={isNewHotkeySystemEnabled ? stopKeyDownPropagation : undefined}
        >
          {isNewHotkeySystemEnabled ? (
            <HotkeyProvider eventTargetRef={wrapperRef}>
              <CloseModalHotkey onClose={hideCloseButton ? undefined : closeTopModalPortal}>
                <div className={style['modal-component-wrapper']}>
                  <div
                    className={style['modal-positioning-wrapper-background']}
                    onClick={hideCloseButton ? undefined : onCancelCb}
                    {...testId('modal-background')}
                  />
                  <ModalProvider onCancel={hideCloseButton ? undefined : onCancelCb} onClose={hideCloseButton ? undefined : onCloseCb}>
                    {children}
                  </ModalProvider>
                </div>
              </CloseModalHotkey>
              {globalHotkeys.map((hotkey) => (
                <Hotkey key={hotkey.name} hotkey={hotkey} />
              ))}
            </HotkeyProvider>
          ) : (
            <CloseModalHotkey onClose={hideCloseButton ? undefined : closeTopModalPortal}>
              <div className={style['modal-component-wrapper']}>
                <div
                  className={style['modal-positioning-wrapper-background']}
                  onClick={hideCloseButton ? undefined : onCancelCb}
                  {...testId('modal-background')}
                />
                <ModalProvider onCancel={hideCloseButton ? undefined : onCancelCb} onClose={hideCloseButton ? undefined : onCloseCb}>
                  {children}
                </ModalProvider>
              </div>
            </CloseModalHotkey>
          )}
        </div>
      </GlobalEventListenerRoot>
      {isConfirmModalOpen && (
        <ConfirmDialogueModal
          body={
            <FormattedMessage
              {...confirmCancelBodyMessage}
              values={{
                p: (chunks) => <p>{chunks}</p>,
                br: <br />,
              }}
            />
          }
          buttonMessage={<FormattedMessage {...confirmCancelButtonMessage} />}
          header={<FormattedMessage {...confirmCancelHeaderMessage} />}
          onClose={onCloseConfirmModal}
          onConfirm={onCancel}
        />
      )}
    </div>,
    modalManagerPortalRef,
  );
};

ModalPortal.propTypes = {
  children: PropTypes.node.isRequired,
  hideCloseButton: PropTypes.bool,
  onCancel: PropTypes.func,
  onClose: PropTypes.func.isRequired,
  confirmCancelBodyMessage: MessageDescriptorPropType,
  confirmCancelButtonMessage: MessageDescriptorPropType,
  confirmCancelHeaderMessage: MessageDescriptorPropType,
  shouldConfirmOnClose: PropTypes.func,
};

export default ModalPortal;
